import React from 'react';

import type * as api from '@gitbook/api-client';
import { isSiteSetupConversation } from '@services/content-agents/browser';

import { apiClient, apiResource, usePaginatedAPIQuery } from '../../state';

/** Maximum number of conversations fetched for the history menu. */
const CONVERSATIONS_PAGE_SIZE = 100;

/** Stable empty list reference so consumers can safely use the result as effect/state input. */
const EMPTY_CONVERSATIONS: api.AgentConversation[] = [];

/**
 * List the calling user's organization agent conversations, most recently updated first.
 *
 * These are always private to their creator, so the list needs no visibility filtering: everything
 * it returns belongs to the user asking for it.
 */
export function useOrganizationConversations(
    organizationId: string,
    options?: {
        skip?: boolean;
        /** The site the user is in: only its setup conversation is listed, no other site's. */
        siteId?: string;
    }
): {
    conversations: api.AgentConversation[];
    isLoading: boolean;
    /** Whether older conversations exist beyond the ones listed. */
    hasMore: boolean;
    refetch: () => Promise<unknown>;
} {
    const { data, refetch } = usePaginatedAPIQuery(
        apiResource('listOrganizationAgentConversations', {
            organizationId,
            limit: CONVERSATIONS_PAGE_SIZE,
        }),
        { skip: options?.skip ?? false }
    );

    const siteId = options?.siteId;
    const conversations = React.useMemo(
        () =>
            data?.items.filter(
                (item) =>
                    !isSiteSetupConversation(item.id) ||
                    (siteId !== undefined && isSiteSetupConversation(item.id, siteId))
            ) ?? EMPTY_CONVERSATIONS,
        [data, siteId]
    );

    return {
        conversations,
        isLoading: options?.skip ? false : !data,
        hasMore: Boolean(data?.next),
        refetch,
    };
}

/**
 * Prepare a conversation so it can be connected to, giving the agent the credentials it acts with.
 *
 * This records nothing: the conversation only enters the history once the user sends a first
 * message, so opening the agent and walking away leaves no empty conversation behind.
 */
export async function provisionOrganizationConversation(input: {
    organizationId: string;
    conversationId: string;
}): Promise<void> {
    await apiClient.organizations.provisionOrganizationAgentConversation(
        input.organizationId,
        input.conversationId
    );
}

/**
 * Delete an organization agent conversation, destroying its messages.
 */
export async function deleteOrganizationConversation(input: {
    organizationId: string;
    conversationId: string;
}): Promise<void> {
    await apiClient.organizations.deleteOrganizationAgentConversation(
        input.organizationId,
        input.conversationId
    );
}
